import React from 'react'
import { useQuery } from '@apollo/client'
import { Price } from '@superhi/design'

import { MEMBERSHIP_PRODUCT, MEMBERSHIP_USER_DISCOUNT } from './queries'

import { API } from 'src/api'

export const RECURRING_INTERVAL: Record<API.StripePriceRecurringInterval, string> = {
  DAY: 'day',
  WEEK: 'week',
  MONTH: 'month',
  YEAR: 'year',
}

export const getDiscountDurationText = (
  productPriceRecurring: {
    interval: API.StripePriceRecurringInterval
    intervalCount: number
  } | null,
  durationFrequency: API.MembershipDiscountDuration,
  durationInMonths?: number | null,
  includeTone?: boolean,
) => {
  switch (durationFrequency) {
    case API.MembershipDiscountDuration.ONCE: {
      if (productPriceRecurring) {
        return `${includeTone ? 'your ' : ''}first ${
          RECURRING_INTERVAL[productPriceRecurring.interval]
        }`
      }

      break
    }

    case API.MembershipDiscountDuration.REPEATING: {
      if (productPriceRecurring) {
        if (typeof durationInMonths === 'number') {
          return `${includeTone ? 'your ' : ''}first ${durationInMonths} months`
        }
      }

      break
    }

    case API.MembershipDiscountDuration.FOREVER: {
      if (productPriceRecurring) {
        return `life`
      }

      break
    }

    default: {
      return undefined
    }
  }
}

export const useGetPrice = () => {
  const { data: dataUserDiscount } =
    useQuery<API.MembershipProduct_BuyInfo_MembershipProductPriceDiscount>(
      MEMBERSHIP_USER_DISCOUNT,
      {
        ssr: false,
      },
    )

  const { data: membershipProductData } = useQuery<API.MembershipProduct_BuyInfo_MembershipProduct>(
    MEMBERSHIP_PRODUCT,
    { ssr: false },
  )

  if (!membershipProductData?.membershipProduct) {
    return undefined
  }

  const price = membershipProductData.membershipProduct.prices[0]

  const currencyCode = price.currencyCode as API.CurrencyCode

  const currentPrice = price.value

  const productPriceRecurring = price.recurring
  const priceDiscount =
    dataUserDiscount?.membershipProductPriceDiscount?.discount.__typename ===
    'MembershipDiscountCoupon'
      ? dataUserDiscount?.membershipProductPriceDiscount?.discount
      : dataUserDiscount?.membershipProductPriceDiscount?.discount.coupon

  const userDiscount = dataUserDiscount?.membershipProductPriceDiscount
    ? {
        price: dataUserDiscount.membershipProductPriceDiscount.priceDiscounted.value,
        duration: priceDiscount!.duration,
        durationInMonths: priceDiscount!.durationInMonths,
        durationText: getDiscountDurationText(
          productPriceRecurring,
          priceDiscount!.duration,
          priceDiscount!.durationInMonths,
        ),
        durationTextTone: getDiscountDurationText(
          productPriceRecurring,
          priceDiscount!.duration,
          priceDiscount!.durationInMonths,
          true,
        ),
      }
    : undefined

  const withoutUserDiscountPrice = currentPrice
  const payablePrice = typeof userDiscount?.price === 'number' ? userDiscount.price : currentPrice

  const nextPrice = currentPrice

  const display =
    withoutUserDiscountPrice !== payablePrice ? (
      <>
        <s>
          <Price currencyCode={currencyCode}>{withoutUserDiscountPrice}</Price>
        </s>{' '}
        <Price currencyCode={currencyCode}>{payablePrice}</Price>
      </>
    ) : (
      <Price currencyCode={currencyCode}>{payablePrice}</Price>
    )

  return {
    currencyCode,
    recurring: price.recurring,
    fullPrice: withoutUserDiscountPrice,
    payablePrice,
    nextPriceIsDifferent:
      userDiscount && userDiscount.duration !== API.MembershipDiscountDuration.FOREVER,
    userDiscount,
    nextPrice, // the price after the billing cycle
    display,
  }
}
